import axios from 'axios'
import axiosNode from '@/axios-instance-node'
import {
	FullRejectionDetail,
	FullRejectionList,
	PhotosetReviewState,
	UploaderFolderView
} from '@/features/host-review/types'
import { parseResponseData } from '@/helpers/utils'

const BASE_URL = 'v3/luminate/host-review'

/**
 * A 404 on these endpoints is not a failure: it is how the backend says the
 * feature is off, the photoset never entered a review, or the review is not a
 * full rejection. Callers render today's experience for null rather than an
 * error state, so the distinction has to survive the request layer.
 */
const nullOnNotFound = (error: unknown) => {
	if (axios.isAxiosError(error) && error.response?.status === 404) {
		return null
	}

	throw error
}

/**
 * The photoset's review state, as the route guard keys on it. Returns null when
 * the photoset has no review surface at all, which reads the same as
 * `not_reviewed` to every caller.
 */
export const httpGetPhotosetReviewState = async (
	photosetId: number
): Promise<PhotosetReviewState | null> => {
	const response = await axiosNode
		.get(`${BASE_URL}/photosets/${photosetId}`)
		.catch(nullOnNotFound)

	if (!response) {
		return null
	}

	return parseResponseData({
		responseData: response.data,
		responseShape: PhotosetReviewState
	})
}

/** The uploader's folder view: one approved folder, or kept + removed. */
export const httpGetUploaderFolderView = async (
	photosetId: number
): Promise<UploaderFolderView | null> => {
	const response = await axiosNode
		.get(`${BASE_URL}/photosets/${photosetId}/folders`)
		.catch(nullOnNotFound)

	if (!response) {
		return null
	}

	return parseResponseData({
		responseData: response.data,
		responseShape: UploaderFolderView
	})
}

/**
 * Full-rejection reviews, most recently closed first. Reviews that kept at
 * least one photo - including the ones the deadline closed - never appear.
 */
export const httpGetFullRejections = async (params: {
	page: number
	perPage: number
}): Promise<FullRejectionList | null> => {
	const response = await axiosNode
		.get(`${BASE_URL}/full-rejections`, { params })
		.catch(nullOnNotFound)

	if (!response) {
		return null
	}

	return parseResponseData({
		responseData: response.data,
		responseShape: FullRejectionList
	})
}

/** One full rejection, plus the editor-selected set as the host saw it. */
export const httpGetFullRejection = async (
	reviewId: string
): Promise<FullRejectionDetail | null> => {
	const response = await axiosNode
		.get(`${BASE_URL}/full-rejections/${reviewId}`)
		.catch(nullOnNotFound)

	if (!response) {
		return null
	}

	return parseResponseData({
		responseData: response.data,
		responseShape: FullRejectionDetail
	})
}
